/*
		       FILENAME: generic.service.ts
		          ROUTE: client/src/app/services
	             AUTHOR: ICI/AD
 	               WHAT: THIS IS THE GENERIC SERVICE THAT IS USED TO SEND HTTP REQUESTS TO THE SERVER. IT IS A BASE SERVICE THAT HANDLES THE HTTP REQUESTS.
	                HOW: THIS SERVICE IS USED TO SEND HTTP REQUESTS TO THE SERVER. IT IS A BASE SERVICE THAT HANDLES THE HTTP REQUESTS.
	    IMPORTING FILES: base.service, httpclient, toastrservice, observable, subject, takeuntil
	  SUBSCRIBING FILES: app.component
	   LAST COMMIT DATE: September 26, 2025
*/

// IMPORTING ANGULAR HTTP CLIENT
import {HttpClient} from '@angular/common/http';
import {Inject, Injectable} from '@angular/core';
import {ToastrService} from 'ngx-toastr';
import {Observable, Subject, takeUntil} from 'rxjs';
import {BaseService} from './base.service';
import {DEFAULT_MESSAGES} from './messages/default-messages';

// RESPONSIBLE TO SERVE ALMOST EVERY SERVICE IN ENTIRE PROJECT.
@Injectable ({ providedIn: 'root' })
export class GenericService extends BaseService
{
    // STORE ERROR MESSAGES
	public errorMessages: any = {};

    // STORE SITE VARIABLES
	public siteVariables: any = {};

	// OBSERVABLE TO BE TRIGGERED ON STEPPER SELECTED.
	public onStepperSelected: Subject <any> = new Subject <any> ();

	// OBSERVABLE TO BE TRIGGERED TO CHANGE THE STEPPER AFTER STEP DATA SAVED.
	public onStepperDataSave: Subject <any> = new Subject <any> ();

    // STORE IDLE TIME FOR AUTO LOGOUT.
	public idleLimit: any; // DEFINE IDLE TIME.

    // STORE IDLE TIME FOR AUTO LOGOUT IF USER IS IN ANY FILE LOCATION.
	public fileIdleLimit: any;

	/**
	 * STOP REQUEST TO SERVER
	 *
	 */
	public stopRequest: Subject<void> = new Subject<void>();

	constructor
	(
        @Inject (HttpClient) protected override http: HttpClient,
        private toaster: ToastrService,
	)
	{
		super (http);
	}

    /**
     * SEND GET REQUEST TO SERVER.
     *
     * @method get
     * @param url string
     */
	public get(url:string): Observable<any>
	{
		return this._get (url).pipe (takeUntil (this.stopRequest));
	}

    /**
     * SEND PUT REQUEST TO SERVER.
     *
     * @method put
     * @param url string
     * @param putBody object
     */
	public put(url:string, putBody:object): Observable<any>
	{
		return this._put (url, putBody).pipe (takeUntil (this.stopRequest));
	}

    /**
     * SEND PUT REQUEST TO OTHER SERVER.
     *
     * @method put
     * @param url string
     * @param putBody object
     */
	public putRequestToOtherServer(url:string, putBody:object): Observable<any>
	{
		return this._putRequestToOtherServer (url, putBody).pipe (takeUntil (this.stopRequest));
	}

    /**
     * SEND POST REQUEST TO SERVER.
     *
     * @method post
     * @param url string
     * @param postBody object
     */
	public post(url:string, postBody:object): Observable<any>
	{
		return this._post (url, postBody).pipe (takeUntil (this.stopRequest));
	}

    /**
     * SEND PATCH REQUEST TO SERVER.
     *
     * @method post
     * @param url string
     * @param postBody object
     */
	public patch(url:string, postBody:object): Observable<any>
	{
		return this._patch (url, postBody).pipe (takeUntil (this.stopRequest));
	}

    /**
     * SEND DELETE REQUEST TO SERVER.
     *
     * @method delete
     * @param url string
     */
	public delete(url:string, body: object): Observable<any>
	{
		return this._delete (url, body).pipe (takeUntil (this.stopRequest));
	}

    /**
     * GET SITE MESSAGES
     *
     */
	getSiteMessages()
	{
		this.get ('default-error-messages').pipe (takeUntil (this.stopRequest)).subscribe
		({
			next: (response) =>
        	{
				// SET ERROR MESSAGES
        		this.errorMessages = response.data;
        	},
			error: (err) =>
        	{
            	this.toaster.error ('Unable to get response from server');
        	}
		});
	}

    /**
     * RESOLVE A MSG-* SITE MESSAGE KEY. NEVER RETURNS UNDEFINED.
     *
     * ORDER: THE DATABASE ROW, THEN THE BUILT-IN DEFAULT, THEN THE CALLER'S FALLBACK. THE
     * MESSAGES ARE FETCHED ASYNCHRONOUSLY AT BOOT, SO A DIRECT INDEX INTO errorMessages IS
     * undefined FOR THE FIRST MOMENTS OF THE SESSION AND FOREVER FOR ANY KEY WITH NO ROW.
     *
     * @param key THE MSG-* KEY
     * @param fallback USED WHEN NEITHER THE DATABASE NOR THE BUILT-IN MAP HAS THE KEY
     */
	public msg (key: string, fallback = ''): string
	{
		const value = this.errorMessages?.[key];

		if (typeof value === 'string' && value.trim ())
		{
			return value;
		}

		return DEFAULT_MESSAGES[key] ?? fallback;
	}

    /**
     * GET SITE VARIABLES
     *
     */
	getSiteVariables()
	{
		this.get ('get-site-variables').subscribe
		({
			next: (response) =>
        	{
				// SET SITE VARIABLES
            	this.siteVariables = response.data;

                // SITE VARIABLE VALUE IS IN MINUTE. SO CONVERTING TO MILLISECONDS.
            	this.idleLimit = parseInt (this.siteVariables['SV-IDLE-AUTO-LOGOUT-TIME']) * 60 * 1000;

                // SITE VARIABLE VALUE IS IN MINUTE. SO CONVERTING TO MILLISECONDS.
                // STORE IDLE TIME FOR AUTO LOGOUT IF USER IS IN ANY FILE LOCATION.
            	this.fileIdleLimit = parseInt (this.siteVariables['SV-IDLE-AUTO-TIMEOUT-INFILE']) * 60 * 1000;
        	},
			error: (err) =>
        	{
            	this.toaster.error ('Unable to get response from server');
        	}
		});
	}
}
